import { useState } from 'react'
import { FileText, Package, Tags, Target } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

export interface ManualProductDraft {
  product_name: string
  description: string
  selling_points: string[]
  target_market: string
  target_audience: string
  price: string
  language: string
  keywords: string[]
  competitor_brands: string[]
}

interface ManualProductEditorProps {
  value: ManualProductDraft
  onChange: (value: ManualProductDraft) => void
  disabled?: boolean
  density?: 'default' | 'compact'
  basicSectionTitle?: string
  marketSectionTitle?: string
  sellingPointsLabel?: string
  sellingPointsPlaceholder?: string
  descriptionPlaceholder?: string
  showKeywords?: boolean
  showCompetitors?: boolean
}

function appendIfPresent(items: string[], nextValue: string): string[] {
  const trimmed = nextValue.trim()
  if (!trimmed) {
    return items
  }
  return [...items, trimmed]
}

export function ManualProductEditor({
  value,
  onChange,
  disabled = false,
  density = 'default',
  basicSectionTitle = 'Basic Information',
  marketSectionTitle = 'Target Market',
  sellingPointsLabel = 'Selling Points',
  sellingPointsPlaceholder = 'Add a selling point...',
  descriptionPlaceholder = 'Describe your product...',
  showKeywords = true,
  showCompetitors = true,
}: ManualProductEditorProps) {
  const [newKeyword, setNewKeyword] = useState('')
  const [newSellingPoint, setNewSellingPoint] = useState('')
  const [newCompetitor, setNewCompetitor] = useState('')
  const isCompact = density === 'compact'

  const containerSpacingClass = isCompact ? 'space-y-3' : 'space-y-6'
  const cardSpacingClass = isCompact ? 'p-3.5 space-y-3' : 'p-6 space-y-6'
  const sectionTitleClass = isCompact ? 'text-sm font-semibold leading-none' : 'text-lg font-semibold'
  const gridGapClass = isCompact ? 'gap-3' : 'gap-6'
  const inputIconClass = isCompact ? 'w-4 h-4' : 'w-5 h-5'
  const inputPaddingClass = isCompact ? 'pl-9' : 'pl-10'
  const labelClass = isCompact ? 'block text-xs font-medium text-gray-700 mb-0.5' : 'block text-sm font-medium text-gray-700 mb-1'
  const inputClass = isCompact ? 'h-9 text-sm' : ''
  const addRowClass = isCompact ? 'flex gap-1.5 mb-1' : 'flex gap-2 mb-2'
  const tagContainerGapClass = isCompact ? 'flex flex-wrap gap-1' : 'flex flex-wrap gap-2'
  const textareaRows = isCompact ? 2 : 3
  const textareaClass = isCompact
    ? 'min-h-[72px] w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary'
    : 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary'
  const pillClass = isCompact
    ? 'px-2 py-0.5 rounded-full text-[11px] leading-4 flex items-center gap-1'
    : 'px-3 py-1 rounded-full text-sm flex items-center gap-1'
  const addButtonProps = isCompact
    ? { size: 'sm' as const, className: 'h-9 px-2.5 text-xs' }
    : {}

  const updateList = (field: 'keywords' | 'selling_points' | 'competitor_brands', nextItems: string[]) => {
    onChange({
      ...value,
      [field]: nextItems,
    })
  }

  const removeFromList = (field: 'keywords' | 'selling_points' | 'competitor_brands', index: number) => {
    updateList(
      field,
      value[field].filter((_, currentIndex) => currentIndex !== index)
    )
  }

  return (
    <div className={containerSpacingClass}>
      <div className={`bg-white rounded-xl border border-gray-200 ${cardSpacingClass}`}>
        <h2 className={sectionTitleClass}>{basicSectionTitle}</h2>

        <div className={`grid grid-cols-2 ${gridGapClass}`}>
          <div>
            <label className={labelClass}>Product Name *</label>
            <div className="relative">
              <Package className={`absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 ${inputIconClass}`} />
              <Input
                required
                placeholder="Enter product name"
                value={value.product_name}
                onChange={(event) =>
                  onChange({
                    ...value,
                    product_name: event.target.value,
                  })
                }
                className={`${inputPaddingClass} ${inputClass}`.trim()}
                disabled={disabled}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Price</label>
            <Input
              placeholder="$0.00"
              value={value.price}
              onChange={(event) =>
                onChange({
                    ...value,
                    price: event.target.value,
                  })
              }
              className={inputClass}
              disabled={disabled}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Description</label>
          <textarea
            rows={textareaRows}
            placeholder={descriptionPlaceholder}
            value={value.description}
            onChange={(event) =>
              onChange({
                ...value,
                description: event.target.value,
              })
            }
            className={textareaClass}
            disabled={disabled}
          />
        </div>

        <div>
          <label className={labelClass}>{sellingPointsLabel}</label>
          <div className={addRowClass}>
            <Input
              placeholder={sellingPointsPlaceholder}
              value={newSellingPoint}
              onChange={(event) => setNewSellingPoint(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  const nextItems = appendIfPresent(value.selling_points, newSellingPoint)
                  if (nextItems !== value.selling_points) {
                    updateList('selling_points', nextItems)
                    setNewSellingPoint('')
                  }
                }
              }}
              className={inputClass}
              disabled={disabled}
            />
            <Button
              type="button"
              variant="outline"
              size={addButtonProps.size}
              className={addButtonProps.className}
              onClick={() => {
                const nextItems = appendIfPresent(value.selling_points, newSellingPoint)
                if (nextItems !== value.selling_points) {
                  updateList('selling_points', nextItems)
                  setNewSellingPoint('')
                }
              }}
              disabled={disabled}
            >
              Add
            </Button>
          </div>
          <div className={tagContainerGapClass}>
            {value.selling_points.map((point, index) => (
              <span
                key={`${point}-${index}`}
                className={`${pillClass} bg-blue-50 text-blue-700`}
              >
                {point}
                <button
                  type="button"
                  onClick={() => removeFromList('selling_points', index)}
                  className="hover:text-blue-900"
                  disabled={disabled}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className={`bg-white rounded-xl border border-gray-200 ${cardSpacingClass}`}>
        <h2 className={sectionTitleClass}>{marketSectionTitle}</h2>

        <div className={`grid grid-cols-2 ${gridGapClass}`}>
          <div>
            <label className={labelClass}>
              <Target className="w-4 h-4 inline mr-1" />
              Target Market
            </label>
            <Input
              placeholder="e.g., North America, Europe"
              value={value.target_market}
              onChange={(event) =>
                onChange({
                    ...value,
                    target_market: event.target.value,
                  })
              }
              className={inputClass}
              disabled={disabled}
            />
          </div>

          <div>
            <label className={labelClass}>Target Audience</label>
            <Input
              placeholder="e.g., Young professionals, 25-35"
              value={value.target_audience}
              onChange={(event) =>
                onChange({
                    ...value,
                    target_audience: event.target.value,
                  })
              }
              className={inputClass}
              disabled={disabled}
            />
          </div>
        </div>

        {showKeywords && (
          <div>
            <label className={labelClass}>
              <Tags className="w-4 h-4 inline mr-1" />
              Keywords
            </label>
            <div className={addRowClass}>
              <Input
                placeholder="Add a keyword..."
                value={newKeyword}
                onChange={(event) => setNewKeyword(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    const nextItems = appendIfPresent(value.keywords, newKeyword)
                    if (nextItems !== value.keywords) {
                      updateList('keywords', nextItems)
                      setNewKeyword('')
                    }
                  }
                }}
                className={inputClass}
                disabled={disabled}
              />
              <Button
                type="button"
                variant="outline"
                size={addButtonProps.size}
                className={addButtonProps.className}
                onClick={() => {
                  const nextItems = appendIfPresent(value.keywords, newKeyword)
                  if (nextItems !== value.keywords) {
                    updateList('keywords', nextItems)
                    setNewKeyword('')
                  }
                }}
                disabled={disabled}
              >
                Add
              </Button>
            </div>
            <div className={tagContainerGapClass}>
              {value.keywords.map((keyword, index) => (
                <span
                  key={`${keyword}-${index}`}
                  className={`${pillClass} bg-primary/10 text-primary`}
                >
                  {keyword}
                  <button
                    type="button"
                    onClick={() => removeFromList('keywords', index)}
                    className="hover:text-primary/80"
                    disabled={disabled}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {showCompetitors && (
          <div>
            <label className={labelClass}>Competitor Brands</label>
            <div className={addRowClass}>
              <Input
                placeholder="Add a competitor brand..."
                value={newCompetitor}
                onChange={(event) => setNewCompetitor(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    const nextItems = appendIfPresent(value.competitor_brands, newCompetitor)
                    if (nextItems !== value.competitor_brands) {
                      updateList('competitor_brands', nextItems)
                      setNewCompetitor('')
                    }
                  }
                }}
                className={inputClass}
                disabled={disabled}
              />
              <Button
                type="button"
                variant="outline"
                size={addButtonProps.size}
                className={addButtonProps.className}
                onClick={() => {
                  const nextItems = appendIfPresent(value.competitor_brands, newCompetitor)
                  if (nextItems !== value.competitor_brands) {
                    updateList('competitor_brands', nextItems)
                    setNewCompetitor('')
                  }
                }}
                disabled={disabled}
              >
                Add
              </Button>
            </div>
            <div className={tagContainerGapClass}>
              {value.competitor_brands.map((brand, index) => (
                <span
                  key={`${brand}-${index}`}
                  className={`${pillClass} bg-orange-50 text-orange-700`}
                >
                  {brand}
                  <button
                    type="button"
                    onClick={() => removeFromList('competitor_brands', index)}
                    className="hover:text-orange-900"
                    disabled={disabled}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {!showKeywords && !showCompetitors && (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-500">
            <div className="flex items-center gap-2 font-medium text-gray-700">
              <FileText className="h-4 w-4" />
              Optional structured fields are hidden for this flow.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
